import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { useInvoices, useCustomers, useItems, useAI, usePaymentConditions, useCompany } from '../hooks/useApi';
import { formatCurrency, formatDate, parseLocalDate, addDaysYMD, storage } from '../lib/utils';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Plus, 
  Trash2, 
  CalendarIcon, 
  Check, 
  ChevronsUpDown,
  Loader2,
  FileText,
  Save
} from 'lucide-react';
import { cn } from '../lib/utils';

const DRAFT_KEY = 'invoiceflow_draft';

// Standard payment conditions with their due date offset in days
const STANDARD_PAYMENT_CONDITIONS = [
  { text: 'Due on receipt', days: 0 },
  { text: 'Net 7', days: 7 },
  { text: 'Net 15', days: 15 },
  { text: 'Net 30', days: 30 },
  { text: 'Net 45', days: 45 },
  { text: 'Net 60', days: 60 },
];

// Parse payment condition text to get days
const getPaymentDays = (conditionText) => {
  if (!conditionText) return null;
  
  const lowerText = conditionText.toLowerCase().trim();
  
  // Check standard conditions
  const standard = STANDARD_PAYMENT_CONDITIONS.find(
    c => c.text.toLowerCase() === lowerText
  );
  if (standard) return standard.days;
  
  // Try to parse "Net X" pattern
  const netMatch = lowerText.match(/net\s*(\d+)/i);
  if (netMatch) return parseInt(netMatch[1], 10);
  
  // "Due on receipt" variations
  if (lowerText.includes('receipt') || lowerText.includes('immediate')) return 0;
  
  return null;
};

// Calculate due date from issue date and payment condition
const calculateDueDate = (issueDate, paymentCondition) => {
  const days = getPaymentDays(paymentCondition);
  if (days === null || !issueDate) return null;
  
  return addDaysYMD(issueDate, days);
};

// Check if draft exists (exported for use in other components)
export const hasDraft = () => {
  const draft = storage.get(DRAFT_KEY);
  return draft && (draft.customer_name || draft.line_items?.some(item => item.description));
};

const getInitialForm = () => {
  // Try to restore from draft
  const draft = storage.get(DRAFT_KEY);
  if (draft) {
    return { draft, hasDraft: true };
  }
  return {
    draft: {
      customer_id: '',
      customer_name: '',
      customer_address: '',
      customer_city: '',
      customer_postal_code: '',
      customer_country: '',
      customer_tax_id: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, unit: 'pcs' }],
      tax_rate: 0,
      currency: 'USD',
      issue_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      footer_notes: '',
      payment_conditions: ''
    },
    hasDraft: false
  };
};

const NewInvoicePage = () => {
  const navigate = useNavigate();
  const { createInvoice, loading: creating } = useInvoices();
  const { getCustomers } = useCustomers();
  const { getItems } = useItems();
  const { extractInvoice, loading: extracting } = useAI();
  const { getConditions } = usePaymentConditions();
  const { getCompany } = useCompany();

  // State
  const [aiText, setAiText] = useState('');
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [company, setCompany] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [itemSearches, setItemSearches] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeoutRef = useRef(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // Invoice form state - restore from draft if available
  const [form, setForm] = useState(() => {
    const initial = getInitialForm();
    return initial.draft;
  });

  // Show toast when draft is restored
  useEffect(() => {
    const initial = getInitialForm();
    if (initial.hasDraft && !draftRestored) {
      setDraftRestored(true);
      toast.info('Draft restored! Your previous work has been recovered.', {
        duration: 5000,
      });
    }
  }, [draftRestored]);

  // Auto-save draft to localStorage
  useEffect(() => {
    // Debounce save - wait 1 second after last change
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      storage.set(DRAFT_KEY, form);
      setLastSaved(new Date());
    }, 1000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [form]);

  // Clear draft on successful submission
  const clearDraft = () => {
    storage.remove(DRAFT_KEY);
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      const [customersRes, itemsRes, conditionsRes, companyRes] = await Promise.all([
        getCustomers(),
        getItems(),
        getConditions(),
        getCompany()
      ]);
      
      if (customersRes.data) setCustomers(customersRes.data);
      if (itemsRes.data) setItems(itemsRes.data);
      if (conditionsRes.data) setConditions(conditionsRes.data);
      if (companyRes.data) {
        setCompany(companyRes.data);
        // Only set currency if no draft was restored
        const draft = storage.get(DRAFT_KEY);
        if (!draft && companyRes.data.default_currency) {
          setForm(f => ({ ...f, currency: companyRes.data.default_currency }));
        }
      }
    };
    loadData();
  }, []);

  // Update conditions when customer changes
  useEffect(() => {
    if (form.customer_id) {
      getConditions(form.customer_id).then(res => {
        if (res.data) setConditions(res.data);
      });
    }
  }, [form.customer_id]);

  // Handlers
  const handleAIExtract = async () => {
    if (!aiText.trim()) {
      toast.error('Please enter some text to extract');
      return;
    }

    const { data, error } = await extractInvoice(aiText);
    if (error) return;

    // Update form with extracted data - ADD line items instead of replacing
    setForm(prev => {
      // Filter out empty line items from previous
      const existingItems = prev.line_items.filter(item => item.description.trim() !== '');
      // Get new items from extraction
      const newItems = data.line_items?.length > 0 ? data.line_items : [];
      // Merge: existing + new, or just new if no existing
      const mergedItems = existingItems.length > 0 
        ? [...existingItems, ...newItems]
        : (newItems.length > 0 ? newItems : [{ description: '', quantity: 1, unit_price: 0, unit: 'pcs' }]);
      
      return {
        ...prev,
        customer_name: data.customer_name || prev.customer_name,
        customer_address: data.customer_address || prev.customer_address,
        customer_city: data.customer_city || prev.customer_city,
        customer_postal_code: data.customer_postal_code || prev.customer_postal_code,
        customer_country: data.customer_country || prev.customer_country,
        customer_tax_id: data.customer_tax_id || prev.customer_tax_id,
        line_items: mergedItems,
        tax_rate: data.tax_rate || prev.tax_rate,
        currency: data.currency || prev.currency,
        issue_date: data.issue_date || prev.issue_date,
        due_date: data.due_date || prev.due_date,
        footer_notes: data.footer_notes || prev.footer_notes,
        payment_conditions: data.payment_conditions || prev.payment_conditions
      };
    });

    setAiText(''); // Clear the input after extraction
    toast.success('Invoice data extracted and added!');
  };

  const handleCustomerSelect = (customer) => {
    setForm(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_address: customer.address,
      customer_city: customer.city,
      customer_postal_code: customer.postal_code,
      customer_country: customer.country,
      customer_tax_id: customer.tax_id
    }));
    setCustomerOpen(false);
  };

  const handleLineItemChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleItemSelect = (index, item) => {
    handleLineItemChange(index, 'description', item.name);
    handleLineItemChange(index, 'unit_price', item.default_price);
    handleLineItemChange(index, 'unit', item.unit);
    setItemSearches(prev => ({ ...prev, [index]: false }));
  };

  const addLineItem = () => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0, unit: 'pcs' }]
    }));
  };

  const removeLineItem = (index) => {
    if (form.line_items.length === 1) return;
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = useCallback(() => {
    const subtotal = form.line_items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0
    );
    const taxAmount = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  }, [form.line_items, form.tax_rate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_name) {
      toast.error('Customer name is required');
      return;
    }

    if (form.line_items.every(item => !item.description)) {
      toast.error('At least one line item is required');
      return;
    }

    const { data, error } = await createInvoice(form);
    if (error) return;

    clearDraft(); // Clear the draft on successful submission
    toast.success(`Invoice #${data.invoice_number} created!`);
    navigate(`/invoices/${data.id}`);
  };

  const handleClearDraft = () => {
    clearDraft();
    setForm({
      customer_id: '',
      customer_name: '',
      customer_address: '',
      customer_city: '',
      customer_postal_code: '',
      customer_country: '',
      customer_tax_id: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, unit: 'pcs' }],
      tax_rate: 0,
      currency: company?.default_currency || 'USD',
      issue_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      footer_notes: '',
      payment_conditions: ''
    });
    toast.success('Draft cleared');
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="new-invoice-page">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/3 p-6 border-r border-slate-100 overflow-y-auto bg-slate-50/50">
        <div className="space-y-6">
          {/* Auto-save status */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Save className="w-4 h-4" />
              {lastSaved ? (
                <span>Draft saved</span>
              ) : (
                <span>Auto-saving...</span>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClearDraft}
              className="text-muted-foreground hover:text-destructive"
              data-testid="clear-draft-btn"
            >
              Clear Draft
            </Button>
          </div>

          {/* AI Extraction */}
          <Card className="border-accent/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent" />
                AI Text Extraction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                data-testid="ai-extract-input"
                placeholder="Paste an email or invoice text here, and AI will extract the details..."
                className="min-h-[100px] resize-none chat-input"
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
              />
              <Button 
                data-testid="ai-extract-btn"
                onClick={handleAIExtract}
                disabled={extracting || !aiText.trim()}
                className="w-full gap-2"
              >
                {extracting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Extract Invoice Data
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Customer Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Customer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="customer-select-trigger"
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOpen}
                    className="w-full justify-between"
                  >
                    {form.customer_name || "Select or type customer..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search customers..." />
                    <CommandList>
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            onSelect={() => handleCustomerSelect(customer)}
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.customer_id === customer.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {customer.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    data-testid="customer-name-input"
                    value={form.customer_name}
                    onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tax ID</Label>
                  <Input
                    data-testid="customer-tax-id-input"
                    value={form.customer_tax_id}
                    onChange={(e) => setForm(f => ({ ...f, customer_tax_id: e.target.value }))}
                    placeholder="Tax ID"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input
                  data-testid="customer-address-input"
                  value={form.customer_address}
                  onChange={(e) => setForm(f => ({ ...f, customer_address: e.target.value }))}
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  data-testid="customer-city-input"
                  value={form.customer_city}
                  onChange={(e) => setForm(f => ({ ...f, customer_city: e.target.value }))}
                  placeholder="City"
                />
                <Input
                  data-testid="customer-postal-input"
                  value={form.customer_postal_code}
                  onChange={(e) => setForm(f => ({ ...f, customer_postal_code: e.target.value }))}
                  placeholder="Postal"
                />
                <Input
                  data-testid="customer-country-input"
                  value={form.customer_country}
                  onChange={(e) => setForm(f => ({ ...f, customer_country: e.target.value }))}
                  placeholder="Country"
                />
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {form.line_items.map((item, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 relative">
                      <Input
                        data-testid={`line-item-desc-${index}`}
                        value={item.description}
                        onChange={(e) => {
                          handleLineItemChange(index, 'description', e.target.value);
                          setItemSearches(prev => ({ ...prev, [index]: true }));
                        }}
                        placeholder="Description"
                        onFocus={() => setItemSearches(prev => ({ ...prev, [index]: true }))}
                        onBlur={() => {
                          // Delay closing to allow click on suggestion
                          setTimeout(() => setItemSearches(prev => ({ ...prev, [index]: false })), 200);
                        }}
                        autoComplete="off"
                      />
                      {/* Dropdown suggestions */}
                      {itemSearches[index] && item.description && items.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {items
                            .filter(i => i.name.toLowerCase().includes(item.description.toLowerCase()))
                            .slice(0, 5)
                            .map((savedItem) => (
                              <div
                                key={savedItem.id}
                                className="px-3 py-2 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleItemSelect(index, savedItem);
                                }}
                                data-testid={`saved-item-${savedItem.id}`}
                              >
                                <span className="text-sm">{savedItem.name}</span>
                                <span className="text-muted-foreground font-mono text-xs">
                                  {formatCurrency(savedItem.default_price, form.currency)}
                                </span>
                              </div>
                            ))}
                          {items.filter(i => i.name.toLowerCase().includes(item.description.toLowerCase())).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              No matching items - type to create new
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeLineItem(index)}
                      disabled={form.line_items.length === 1}
                      data-testid={`remove-line-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        data-testid={`line-item-qty-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit Price</Label>
                      <Input
                        data-testid={`line-item-price-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Input
                        data-testid={`line-item-unit-${index}`}
                        value={item.unit}
                        onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                        placeholder="pcs"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={addLineItem}
                data-testid="add-line-item-btn"
              >
                <Plus className="w-4 h-4" />
                Add Line Item
              </Button>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Currency</Label>
                  <Select 
                    value={form.currency} 
                    onValueChange={(v) => setForm(f => ({ ...f, currency: v }))}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="CZK">CZK (Kč)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tax Rate (%)</Label>
                  <Input
                    data-testid="tax-rate-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.tax_rate}
                    onChange={(e) => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Issue Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        data-testid="issue-date-trigger"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !form.issue_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.issue_date ? formatDate(form.issue_date) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.issue_date ? parseLocalDate(form.issue_date) : undefined}
                        onSelect={(date) => setForm(f => ({ 
                          ...f, 
                          issue_date: date ? format(date, 'yyyy-MM-dd') : '' 
                        }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs">Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        data-testid="due-date-trigger"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !form.due_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.due_date ? formatDate(form.due_date) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.due_date ? parseLocalDate(form.due_date) : undefined}
                        onSelect={(date) => setForm(f => ({ 
                          ...f, 
                          due_date: date ? format(date, 'yyyy-MM-dd') : '' 
                        }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label className="text-xs">Payment Conditions</Label>
                <Select 
                  value={form.payment_conditions} 
                  onValueChange={(v) => {
                    const newDueDate = calculateDueDate(form.issue_date, v);
                    setForm(f => ({ 
                      ...f, 
                      payment_conditions: v,
                      ...(newDueDate ? { due_date: newDueDate } : {})
                    }));
                  }}
                >
                  <SelectTrigger data-testid="payment-conditions-select">
                    <SelectValue placeholder="Select payment terms..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Standard conditions */}
                    {STANDARD_PAYMENT_CONDITIONS.map((cond) => (
                      <SelectItem key={cond.text} value={cond.text}>
                        {cond.text}
                      </SelectItem>
                    ))}
                    {/* User's saved conditions (excluding duplicates) */}
                    {conditions
                      .filter(cond => !STANDARD_PAYMENT_CONDITIONS.some(s => s.text.toLowerCase() === cond.text.toLowerCase()))
                      .map((cond) => (
                        <SelectItem key={cond.id} value={cond.text}>
                          {cond.text}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="payment-conditions-input"
                  value={form.payment_conditions}
                  onChange={(e) => setForm(f => ({ ...f, payment_conditions: e.target.value }))}
                  placeholder="Or type custom conditions..."
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-xs">Footer Notes</Label>
                <Textarea
                  data-testid="footer-notes-input"
                  value={form.footer_notes}
                  onChange={(e) => setForm(f => ({ ...f, footer_notes: e.target.value }))}
                  placeholder="Thank you for your business!"
                  className="resize-none"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            data-testid="create-invoice-submit"
            onClick={handleSubmit}
            disabled={creating}
            className="w-full h-12 gap-2 bg-accent hover:bg-accent/90"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                Create Invoice
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Right Panel - Preview */}
      <div className="hidden lg:block flex-1 p-8 bg-slate-100/50 overflow-y-auto">
        <div className="invoice-preview bg-white rounded-xl shadow-lg p-8 max-w-[700px] mx-auto">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-start gap-4">
              {company?.logo_base64 && (
                <img 
                  src={company.logo_base64} 
                  alt="Company Logo" 
                  className="w-16 h-16 object-contain"
                />
              )}
              <div>
                {company?.name && (
                  <h2 className="text-xl font-bold text-slate-900">{company.name}</h2>
                )}
                {company?.address && <p className="text-sm text-slate-600">{company.address}</p>}
                {(company?.city || company?.postal_code) && (
                  <p className="text-sm text-slate-600">
                    {company.city} {company.postal_code}
                  </p>
                )}
                {company?.country && <p className="text-sm text-slate-600">{company.country}</p>}
                {company?.tax_id && (
                  <p className="text-sm text-slate-600">Tax ID: {company.tax_id}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-extrabold text-slate-900">INVOICE</h1>
              <p className="font-mono text-lg text-accent">#DRAFT</p>
              <p className="text-sm text-slate-600 mt-2">
                Issue: {form.issue_date ? formatDate(form.issue_date) : '-'}
              </p>
              <p className="text-sm text-slate-600">
                Due: {form.due_date ? formatDate(form.due_date) : '-'}
              </p>
            </div>
          </div>

          {/* Bill To */}
          <div className="mb-8">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Bill To
            </h3>
            <p className="font-semibold text-slate-900">{form.customer_name || 'Customer Name'}</p>
            {form.customer_address && <p className="text-sm text-slate-600">{form.customer_address}</p>}
            {(form.customer_city || form.customer_postal_code) && (
              <p className="text-sm text-slate-600">
                {form.customer_city} {form.customer_postal_code}
              </p>
            )}
            {form.customer_country && <p className="text-sm text-slate-600">{form.customer_country}</p>}
            {form.customer_tax_id && (
              <p className="text-sm text-slate-600">Tax ID: {form.customer_tax_id}</p>
            )}
          </div>

          {/* Line Items Table */}
          <div className="mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2">
                    Description
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2">
                    Qty
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2">
                    Unit
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2">
                    Price
                  </th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((item, index) => (
                  <tr key={index} className="border-b border-slate-50">
                    <td className="py-3 text-sm">{item.description || '-'}</td>
                    <td className="py-3 text-sm text-right font-mono">{item.quantity}</td>
                    <td className="py-3 text-sm text-right">{item.unit}</td>
                    <td className="py-3 text-sm text-right font-mono">
                      {formatCurrency(item.unit_price, form.currency)}
                    </td>
                    <td className="py-3 text-sm text-right font-mono">
                      {formatCurrency(item.quantity * item.unit_price, form.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-mono">{formatCurrency(subtotal, form.currency)}</span>
              </div>
              {form.tax_rate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Tax ({form.tax_rate}%)</span>
                  <span className="font-mono">{formatCurrency(taxAmount, form.currency)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-900">
                <span>Total</span>
                <span className="font-mono">{formatCurrency(total, form.currency)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          {(form.payment_conditions || form.footer_notes) && (
            <div className="border-t border-slate-100 pt-6 space-y-3 text-sm text-slate-600">
              {form.payment_conditions && (
                <div>
                  <span className="font-semibold">Payment Terms: </span>
                  {form.payment_conditions}
                </div>
              )}
              {form.footer_notes && (
                <div>
                  <span className="font-semibold">Notes: </span>
                  <span className="whitespace-pre-wrap">{form.footer_notes}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewInvoicePage;
